import type { SortState } from '@/lib/tableSort';

import type {
    AircraftSearchResult,
    AircraftSearchResultGroup,
    AircraftSearchSort,
    AircraftSearchSortColumn,
} from './types';

/** Placeholder wording; the results page's `MATCH_TYPE_DISPLAY` is this map again. */
const MATCH_TYPE_LABELS: Record<AircraftSearchResult['matchType'], string> = {
    body: 'Document text',
    filename: 'File name',
    doc_type: 'Document type',
};

/** Body first: it is the match a reader is usually hunting for. */
const MATCH_TYPE_ORDER = ['body', 'filename', 'doc_type'] as const;

export function groupByMatchType(
    results: AircraftSearchResult[],
): AircraftSearchResultGroup[] {
    return MATCH_TYPE_ORDER.map((matchType) => ({
        label: MATCH_TYPE_LABELS[matchType],
        results: results.filter((result) => result.matchType === matchType),
    })).filter((group) => group.results.length > 0);
}

function isSameRow(
    result: AircraftSearchResult,
    other: AircraftSearchResult,
): boolean {
    return result.docId === other.docId && result.matchType === other.matchType;
}

/** Back into the groups, not onto the hovered row: the panel looks rows up by key. */
export function withPreviewUrl(
    groups: AircraftSearchResultGroup[],
    hoveredResult: AircraftSearchResult | null,
    previewUrl?: string,
): AircraftSearchResultGroup[] {
    if (!hoveredResult) return groups;
    return groups.map((group) => ({
        ...group,
        results: group.results.map((result) =>
            isSameRow(result, hoveredResult)
                ? { ...result, previewUrl }
                : result,
        ),
    }));
}

/** Each sortable column's `?sort` name; the sort types and values all derive from it. */
export const SORT_PARAM_BY_COLUMN = {
    document: 'document',
    matchType: 'match_type',
} as const;

type SortParam = (typeof SORT_PARAM_BY_COLUMN)[AircraftSearchSortColumn];

const SORT_COLUMNS = Object.keys(
    SORT_PARAM_BY_COLUMN,
) as AircraftSearchSortColumn[];

const COLUMN_BY_SORT_PARAM = Object.fromEntries(
    SORT_COLUMNS.map((column) => [SORT_PARAM_BY_COLUMN[column], column]),
) as Record<SortParam, AircraftSearchSortColumn>;

export const AIRCRAFT_SEARCH_SORTS: readonly AircraftSearchSort[] =
    SORT_COLUMNS.flatMap((column) => [
        `${SORT_PARAM_BY_COLUMN[column]}_asc` as const,
        `${SORT_PARAM_BY_COLUMN[column]}_desc` as const,
    ]);

function isSortColumn(key: string): key is AircraftSearchSortColumn {
    return Object.hasOwn(SORT_PARAM_BY_COLUMN, key);
}

/** `?sort` as the results table's sort state; absent is the default grouping. */
export function aircraftSearchSortToState(
    sort: AircraftSearchSort | undefined,
): SortState<AircraftSearchSortColumn> | null {
    if (!sort) return null;
    const separator = sort.lastIndexOf('_');
    return {
        key: COLUMN_BY_SORT_PARAM[sort.slice(0, separator) as SortParam],
        direction: sort.slice(separator + 1) as SortState['direction'],
    };
}

/**
 * The results table's sort state as `?sort`. The default order, or a column
 * the endpoint can't sort, drops it.
 */
export function aircraftSearchSortFromState(
    sort: SortState | null,
): AircraftSearchSort | undefined {
    if (!sort || !isSortColumn(sort.key)) return undefined;
    return `${SORT_PARAM_BY_COLUMN[sort.key]}_${sort.direction}`;
}
